<template>
  <v-container fluid>
    <v-form ref="form" v-model="valid" lazy-validation v-if="application">
      <h4 class="pt-3">{{ $t('labels.personal-info') }}</h4>

      <MessageBox
        class="mt-3 mb-6"
        type="info"
        :closable="true"
        @closed="$emit('setCookie', 'reviewApplicationStep4')"
        v-if="$cookies.get('reviewApplicationStep4') != currentUser.id && currentUser.id"
      >
        <p v-html="$t('company.review.application.messageBox-step4')"></p>
      </MessageBox>
      <v-text-field
        outlined
        class="mt-7"
        :label="$t('fields.firstName')"
        v-model="application.firstName"
        :rules="[rules.required]"
        required
      ></v-text-field>

      <v-text-field
        outlined
        :label="$t('fields.lastName')"
        v-model="application.lastName"
        :rules="[rules.required]"
      ></v-text-field>

      <v-text-field
        outlined
        :label="$t('fields.personalnumber')"
        append-icon="$sl-view"
        class="v-input--is-readonly--light"
        v-model="maskedPersonalnumber"
        :value="application.personalNumber"
        @click:append="showPersonalnumber"
        :rules="[rules.required]"
        readonly
      ></v-text-field>

      <v-text-field
        outlined
        :label="$t('fields.mobilephone')"
        v-model="application.mobilephone"
        :rules="[rules.required, rules.phone]"
      ></v-text-field>

      <v-text-field
        outlined
        :label="$t('fields.email2')"
        v-model="application.email"
        :rules="[rules.required, rules.email]"
      ></v-text-field>
      <v-text-field
        outlined
        :label="$t('fields.address')"
        v-model="application.address1"
        :rules="[rules.required]"
      ></v-text-field>
      <v-text-field
        outlined
        :label="$t('fields.zip')"
        v-model="application.zip"
        :rules="[rules.required, rules.integer, rules.hasLength(5, application.zip)]"
      ></v-text-field>
      <v-text-field
        outlined
        :label="$t('fields.city')"
        v-model="application.city"
        :rules="[rules.required]"
      ></v-text-field>

      <h4 class="mt-6">{{ $t('labels.apprentice-location') }}</h4>

      <MessageBox
        class="mt-3 mb-8"
        type="info"
        :closable="true"
        @closed="$emit('setCookie', 'reviewApplicationStep5')"
        v-if="$cookies.get('reviewApplicationStep5') != currentUser.id && currentUser.id"
      >
        <p v-html="$t('company.review.application.messageBox-step5')"></p>
      </MessageBox>

      <p class="overline">{{ $t('labels.apprentice-type') }}</p>
      <p
        class="mt-n3"
        v-if="selectedApplication.allCoursesApproved == true"
        v-html="$t('labels.apprentice-type-text2')"
      ></p>
      <p v-else class="mt-n3" v-html="$t('labels.apprentice-type-text1')"></p>

      <p class="mt-n3 pb-3" v-html="$t('labels.apprentice-type-text3')"></p>
      
      <!-- department-->
      <template v-if="hasDepartments($route.params.id)">
        <v-select
          :items="companyDepartments"
          v-model="application.departmentId"
          :rules="[rules.required]"
          append-icon="$sl-arrow-down"
          item-text="name"
          item-value="id"
          :label="$t('company.section')"
          outlined
        >
        </v-select>
      </template>

      <!-- workplace -->
      <v-select
        :items="filteredWorkplaces"
        v-model="application.workplaceId"
        :rules="[rules.required]"
        :disabled="(hasDepartments($route.params.id) && !application.departmentId>0) ? true : false"
        item-text="name"
        append-icon="$sl-arrow-down"
        item-value="id"
        :label="$t('labels.workplace')"
        outlined
      >
        <!-- Form instructor -->
        <template v-slot:item="data">
          <!-- `select` -->
          <div class="d-flex flex-column">
            <div class="select__slot__text">{{ data.item.name }}</div>
            <div>
              {{ data.item.postalZip }}, {{ data.item.postalCity }}, ({{
                $tc('labels.apprentice-count', data.item.numApprenticeships)
              }})
            </div>
          </div>
        </template>
      </v-select>

      <v-select
        :items="filteredInstructors"
        v-model="application.instructorId"
        :rules="[rules.required]"
        item-disabled="disableItem"
        item-text="fullName"
        item-value="id"
        :disabled="isDisabledInstructor"
        append-icon=""
        :label="$t('fields.instructor')"
        outlined        
        :menu-props="{value: false}"
        @click="openInstructors()"
        chips
        class=""
      >
        <template v-slot:selection="{ attrs, item, selected }">
          <v-chip
            close-icon="$sl-close"
            v-bind="attrs"
            class="mt-n2"
            :input-value="selected"            
            close
            @click:close="removeInstructor()"
            >{{ item.fullName }}</v-chip
          >
        </template>
      </v-select>

      <ActionButton
        type="application"
        class="mt-n4 mb-8"
        :isDisabled="isDisabledInstructor"
        @click="openInstructors()"      
        actionIcon="$sl-instructor"
      
      >{{ $t('btn.instructor') }}</ActionButton
    >
      <v-select
        :label="$t('fields.employment-type')"
        :items="employmentTypes"
        append-icon="$sl-arrow-down"
        v-model="application.employmentTypeId"
        :rules="[rules.required]"
        item-text="name"
        item-value="id"
        outlined
      ></v-select>

      <DatePicker
        :required="true"
        :label="$t('fields.start-date2')"
        :miniumDate="subtractMonths(18, new Date()).toISOString().substr(0, 10)"
        v-model="application.employmentDate"
        :clearable="true"
        :icon="'$sl-calendar'"
      />
      <MessageBox class="mt-3 mb-6" type="info" :closable="false">
        <p v-html="$t('company.review.application.messageBox-startdate')"></p>
      </MessageBox>
      <template v-if="checkHasNotifications(application)">
        <h4 class="mb-7 mt-10">{{ $t('application.list-notification') }}</h4>
        <MessageBox
          class="mt-n2 mb-6"
          type="info"
          :closable="true"
          @closed="$emit('setCookie', 'reviewApplicationStep6')"
          v-if="$cookies.get('reviewApplicationStep6') != currentUser.id && currentUser.id"
        >
          <p v-html="$t('company.review.application.messageBox-step6')"></p>
        </MessageBox>
        <div class="pb-3" v-for="notification in application.notifications.slice().reverse()" :key="notification.id">
          <div v-if="notification.text != ''">
            <p>
              <b>
                {{ notification.fromName == notification.toName ? $t('fields.fromByn') : notification.fromName }}
                {{ notification.toName ? $t('fields.to') + ' ' + notification.toName : ' BYN' }}
              </b>
              <span class="ligth-grey no-wrap">({{ notification.sentTime | date }})</span>
            </p>
            <p class="mt-n4" v-html="getNotificationText(notification)"></p>
          </div>
        </div>
        <h4 class="pt-4">{{ $t('application.arrangements') }}</h4>
        <MessageBox
          class="mb-10"
          type="info"
          :closable="true"
          @closed="$emit('setCookie', 'reviewApplicationStep7')"
          v-if="$cookies.get('reviewApplicationStep7') != currentUser.id && currentUser.id"
        >
          <p v-html="$t('company.review.application.messageBox-step7')"></p>
        </MessageBox>
      </template>
    </v-form> 
    <ActionButton
      class="mt-6"
      @click="updateApplication"
      :isDisabled="
        application.stateId == 10 || application.stateId == 9 || application.stateId == 3 || application.stateId == 7 || isImitatingUser()
      "
      actionIcon="$sl-save"
      type="application"
      >{{ $t('labels.save') }}</ActionButton
    >
    <v-dialog persistent v-model="openInstructorsModal" max-width="344px">
      <AdminApplicationInstructorModal
        :instructorId="application.instructorId"
        :instructors="filteredInstructors"
        @closeInstructors="closeInstructors"/>
    </v-dialog>
  </v-container>
</template>

<script>
// Entryform for unknown user to become a apprentice
import API from '@/services/api.js'
import MessageBox from '@/components/Common/MessageBox'
import {mapActions, mapGetters} from 'vuex'
import DatePicker from '@/components/Common/DatePicker'
import ActionButton from '@/components/Common/ActionButton'
import pick from 'lodash/pick'
import filter from 'lodash/filter'
import find from 'lodash/find'
import get from 'lodash/get'
import sortBy from 'lodash/sortBy'
import {RulesMixin, InstructorAbilities, CompanyRoles} from '@/components/mixins'
import AdminApplicationInstructorModal from '@/components/Company/AdminApplicationInstructorModal'

// @group Company
// AdminApplicationData component
export default {
  name: 'AdminApplicationData',

  mixins: [RulesMixin, InstructorAbilities, CompanyRoles],

  components: {
    DatePicker,
    ActionButton,
    MessageBox,
    AdminApplicationInstructorModal
  },
  props: {
    // Should fields be editable
    editable: {
      // `true` / `false`
      type: Boolean,
      // The default value is: `true`
      default: true
    },
    allCoursesApproved: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    valid: false,
    notification: '',
    openInstructorsModal: false,
    calendar: false,
    updated: '',
    application: {},
    maskedPersonalnumber: '',
    countries: [],
    isDisabled: false,
    addressDisabled: true,
    dateDisabled: true,
    empTypeDisabled: true,
    professionDisabled: true,
    orientationDisabled: true,
    instructorDisabled: true,
    professions: [],
    professionObj: {},
    minDate: new Date(),
    startDate: '',
    companyNm: '',
    instructors: [],
    employmentTypes: [],
    instructorObj: {},
    employmentObj: {},
    professionOrientationsArr: [],
    lastCompanyId: 0,
    lastProfessionId: 0,
    lastInstructor: 0,
    companyWorkplaces: [],
    companyDepartments: [],
    openLinkWorkplace: false,
    getEmploymentTypesParam: 'apprentice=true',
    fields: [
      'id',
      'employmentDate',
      'firstName',
      'createdAt',
      'employmentTypeId',
      'lastName',
      'address1',
      'address2',
      'mobilephone',
      'email',
      'stateId',
      'state',
      'personalNumber',
      'zip',
      'city',
      'country',
      'step',
      'professionId',
      'instructorId',
      'workplaceId',
      'departmentId',
      'notifications',
      'companyId'
    ]
  }),

  created() {
    this.getProfessions()
    this.initApplication()
    this.getEmploymentTypes()
  },

  filters: {
    fromName(val) {
      if (val) {
        return val + ', '
      } else {
        return ''
      }
    }
  },
  mounted() {
    this.$scrollToTop()
  },
  watch: {
    /**
     * @vuese
     * Used to watch the selectedApplication professionId and call setOrientations
     * @arg called with no argument
     */
    'selectedApplication.professionId'() {
      this.setOrientations()
    },
    
    'application.departmentId'() {
      this.getDepartments()
      this.getWorkplaces()
      this.getInstructors()
    },
  },

  computed: {
    ...mapGetters('application', ['currentStep', 'selectedApplication', 'editableByCompanyStates']),    
    ...mapGetters('currentuser', [
      'currentUser', 
      'isCompanyUser', 
      'notifications', 
      'isAdministrator', 
      'isDepartmentAdministrator', 
      'isCompanyEducationResponsible',
      'isImitatingUser'
    ]),
    ...mapGetters('basic', [
      'basicStates',
      'basicProfessions',
      'getProfessionById',
      'toApproveByCompanyStatesId',
      'hasProfessionOrientations'
    ]),
    isDisabledInstructor(){
    if(this.hasDepartments(this.$route.params.id) && (this.application.workplaceId == null || this.application.workplaceId<1)){
      return true
    }
    else
      return false
    },    
    filteredWorkplaces() {
      if (this.hasDepartments(this.$route.params.id))
        return this.companyWorkplaces.filter(cw => cw.departmentIds.find(d => d == this.application.departmentId))
      return this.companyWorkplaces
    },
    /**
     * @vuese
     * Used get all orientations if that specific profession is a machine profession
     */
    orientations() {
      const prof = this.getProfessionById(this.selectedApplication.professionId)
      return (prof && prof.isMachineProfession) ?
        prof.machineOrientations.map(o => {
          return {value: o.id, text: o.name, defaultIncluded: o.isDefaultIncluded}
        }) :
        []
    },

    filteredInstructors() {
      if (this.hasDepartments(this.$route.params.id)) {
        const applicationDepartmentId = get(this, 'application.departmentId', null)
        const filter = ({ instructor }) => get(instructor, 'instructorDepartmentIds', []).includes(applicationDepartmentId)
        return this.instructors.filter(filter)
      }

      return this.instructors
    }
  },

  methods: {
    ...mapActions('application', ['updateSelectedApplication']),

    subtractMonths(numOfMonths, date = new Date()) {
      date.setMonth(date.getMonth() - numOfMonths);
      return date;
    },

    /**
     * @vuese
     * Used to Initialize application with either selected fields or apply empty fields if none selected
     */
    initApplication() {
      if (this.selectedApplication && this.selectedApplication.id) {
        this.application = pick(this.selectedApplication, this.fields)

        if (this.application.employmentDate) {
          this.startDate = this.application.employmentDate.substr(0, 10)
        }

        if (this.selectedApplication.companyId && this.lastCompanyId != this.selectedApplication.companyId) {
          this.lastCompanyId = this.selectedApplication.companyId
        }

        if (this.selectedApplication.personalNumber && this.selectedApplication.personalNumber.length > 8)
          this.maskedPersonalnumber = this.selectedApplication.personalNumber.substring(0, 8) + '-****'

        if (this.selectedApplication.professionId && this.lastProfessionId != this.selectedApplication.professionId) {
          this.lastProfessionId = this.selectedApplication.professionId
        }
      } else {
        this.application = Object.fromEntries(
          this.fields.map(f => {
            return [f, null]
          })
        )
      }

      this.setOrientations()

      this.application.step = this.currentStep

      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
    showPersonalnumber() {
      this.maskedPersonalnumber =
        this.selectedApplication.personalNumber.substring(0, 8) +
        '-' +
        this.selectedApplication.personalNumber.substring(8, 12)

      return new Promise(resolve => {
        setTimeout(() => {
          this.maskedPersonalnumber = this.selectedApplication.personalNumber.substring(0, 8) + '-****'
        }, 5000)
      })
    },
    closeInstructors(instructorId){
      if(instructorId && instructorId>0)
        this.application.instructorId=instructorId
     // console.log(this.application.instructorId,"emitted::", instructorId)
      this.openInstructorsModal=false
    },
    openInstructors(){    
      this.openInstructorsModal=true      
    },
    removeInstructor() {
    this.application.instructorId=null
    //console.log("remove::")
    },
    /**
     * @vuese
     * Used to remove the url from the notification text
     * @arg The argument is a object
     */
    getNotificationText(note) {
      var noteText = ''
      if (note.concernType == 'Application') {
        var tempText = note.text.split('<a')
        if (tempText.length > 0) {
          noteText = tempText[0]

          tempText = note.text.split('a>')
          if (tempText.length > 1) {
            noteText = noteText + tempText[1]
          }
        }
        noteText = noteText.replace('. .', '.')
      } else {
        noteText = note.text
      }

      return noteText
    },

    /**
     * @vuese
     * Used to check if the application has messages and returm if any
     * @arg The argument is an object representing application
     */
    checkHasNotifications(item) {
      if (item && item.notifications && item.notifications.length > 0) {
        let result = find(item.notifications, o => o.text != '')
        if (result) {
          return true
        }
        return false
      }
      return false
    },
    /**
     * @vuese
     * Used to set orientations for the profession
     */
    setOrientations() {
      if (
        this.selectedApplication &&
        this.selectedApplication.professionOrientations &&
        this.selectedApplication.professionOrientations.length > 0
      ) {
        this.professionOrientationsArr = this.selectedApplication.professionOrientations.map(
          o => o.professionMachineOrientationId
        )
      } else {
        const defaultOrientations = filter(this.orientations, ['isDefaultIncluded', true])
        this.professionOrientationsArr = defaultOrientations.map(o => o.id)
      }
    },
    /**
     * @vuese
     * Used to fetch employment types
     * @arg The argument is an string representing fetching type this case apprentice
     */
    getEmploymentTypes() {
      API.listEmploymentTypes(this.getEmploymentTypesParam)
        .then(response => {
          this.employmentTypes = response.data.map(o => {
            return {
              id: o.id,
              name: o.name
            }
          })

          let empType = this.employmentTypes.find(x => x.id == this.selectedApplication.employmentTypeId)
          if (empType) {
            this.employmentObj = empType
          }
        })
        .catch(error => {
          console.log('Error', error.response)
        })
    },

    /**
     * @vuese
     * Used to fetch instructors
     * @arg The argument is an integer value representing company id
     */
    getInstructors() {
      const scope = this.editableByCompanyStates ? 'active' : 'all'
      
      API.listEmployments(this.selectedApplication.companyId, {type: 'instructor', scope})
        .then(response => {
          const instructors = response.data.map(o => {
            let numGUAppr = 0
            if (o.apprentices) {
              const find = o.apprentices.filter(a => 
              { 
                if(a.education)
                  return   a.education.basicEducationCertificate == null
              })
              numGUAppr = find.length
            }

            return {
              instructor: o,
              id: o.id,
              fullName: o.employed.firstName + ' ' + o.employed.lastName,
              email: o.employed.email,
              employed: o.employed,
              tradeCertificates: o.employed.tradeCertificates,
              numApprentices: o.apprentices ? o.apprentices.length : 0,
              numGUApprentices: numGUAppr,
              showMore: false,
              readInstructionsOn: o.employed.readInstructionsOn,
              canHaveAnotherApprentice: this.canHaveAnotherApprentice(o, 
                  this.selectedApplication.professionId, 
                  this.selectedApplication.professionOrientations.map(ma => ma.professionMachineOrientation.machineId), 
                  !this.selectedApplication.allCoursesApproved),
              disableItem: !Boolean(
                this.canHaveAnotherApprentice(o, 
                  this.selectedApplication.professionId, 
                  this.selectedApplication.professionOrientations.map(ma => ma.professionMachineOrientation.machineId), 
                  !this.selectedApplication.allCoursesApproved) && 
                o.employed.email && o.employed.email!='' &&
                this.haveCertificates(o, 
                  this.selectedApplication.professionId,
                  this.selectedApplication.professionOrientations.map(ma => ma.professionMachineOrientation.machineId), 
                  !this.selectedApplication.allCoursesApproved)
              ),              
              instructorExemptions: o.employed.instructorExemptions || [],
              machineQualifications: o.employed.machinequalifications || [],
              instructorDepartmentIds: o.instructorDepartmentIds
            }
          })

        //  If GU the employee must has role instructor.

          this.instructors=instructors

          if (this.hasDepartments(this.$route.params.id) == true && this.selectedApplication.allCoursesApproved==false) {
            this.instructors = this.instructors.filter(i =>
              i.instructorDepartmentIds.find(d => d == this.application.departmentId)
            )

          }
          let inst = this.instructors.find(x => x.id == this.selectedApplication.instructorId)
          if (inst) {
            this.instructorObj = inst
          }
        })
        .catch(error => {
          console.log('catch Error', error)
        })
    },
    /**
     * @vuese
     * Used to fetch professions and get the selected profession if there is any
     */
    getProfessions() {
      this.professions = this.basicProfessions.map(o => {
        return {id: o.id, text: o.name, maxApp: o.maxApprenticesForInstructor}
      })
      let prof = this.professions.find(x => x.id == this.selectedApplication.professionId)
      if (prof) {
        this.professionObj = prof
      }
    },

    /**
     * @vuese
     * Used fetch the departments by company id
     * @arg The argument is an integer value representing company id
     */
    getDepartments() {
      API.listPublicDepartments(this.$route.params.id)
        .then(response => {
         if (this.isAdministrator) {
            this.companyDepartments = response.data;
          } else {
            let depIds = this.getCurrentUserDepartmentsIds(
              this.$route.params.id,
              this.isDepartmentAdministrator,
              false
            );
            this.companyDepartments = response.data.filter((r) => depIds.find((u) => u == r.id));
          }

          this.companyDepartments = sortBy(this.companyDepartments, 'name')
        })
        .catch(error => {
          console.log('There was an error:', error)
        })
    },
    /**
     * @vuese
     * Used to fetch company
     * @arg The argument is an integer value representing company id
     */
    initCompany() {
      API.getCompany(this.$route.params.id)
        .then(res => this.companyNm = res.data.name)
        .catch(err => console.log(err))
    },

    getWorkplaces() {
      let workplaces = []

      API.getWorkplaces(this.$route.params.id).then(result => {
        workplaces = result.data.map(o => {
          return {
            id: o.id,
            postalAddress1: o.postalAddress1,
            name: o.name,
            postalZip: o.postalZip,
            postalCity: o.postalCity,
            numApprenticeships: o.numApprenticeships,
            departmentIds: o.departmentIds,
            departmentWorkplaces: o.departmentWorkplaces
          }
        })
        // If has deparments, only show the workplaces with the selected department
        if(this.hasDepartments(this.$route.params.id)) {
          this.companyWorkplaces = workplaces.filter(f => f.departmentIds.includes(this.application.departmentId))                
        }      
        else this.companyWorkplaces = workplaces
      })
    },

    /**
     * @vuese
     * Used to update application if the form is valid
     * @arg The argument is an integer representing application  id and params are the selected fields
     */
    updateApplication() {
      if (!this.$refs.form.validate() || !this.application.employmentDate || this.application.employmentDate == '') {
        this.$events.fire('showError', this.$t('errors.form-validation'))
        return
      }

      API.updateApplication(this.application.id, {data: this.application})
        .then(() => {
          /**
           * @vuese
           * Used to update application in vuex store with the application saved
           */
          this.updateSelectedApplication(this.application)
          // Fire info that application is saved
          // @arg The argument is a string
          this.$events.fire('showInfo', this.$t('success.application-updated'))
        })
        .catch(error => {
          this.$events.fire('showError', this.$t('errors.save-failure'))
        })
    }
  }
}
</script>

<style scoped></style>
